import React, { useEffect, useState, useMemo } from 'react'
import Workflow from './Workflow'
import { timelineService } from '../../services/timelineService'
import { useAuthContext } from '../../hooks/useAuthContext'
import { useNavigate } from 'react-router-dom'

const considerDelegation = (pdData, delegationTracker) => {
  if (!Array.isArray(pdData)) return []
  if (!Array.isArray(delegationTracker) || !delegationTracker.length) return pdData

  let finalPdData = pdData.map((it) => ({ ...it }))

  delegationTracker.forEach((delegation, index) => {
    const delegatedTask = finalPdData.find((it) => it.id == delegation.process_id)
    if (!delegatedTask) return

    const delegationNodeId = `${delegatedTask.id}_delegated_${index + 1}`
    const existingFromIds = Array.isArray(delegatedTask.from_process_ids)
      ? [...delegatedTask.from_process_ids]
      : []

    // 1. Update any parent nodes whose to_process_ids point to delegatedTask
    let replacedInParent = false
    finalPdData.forEach((parentNode) => {
      if (
        Array.isArray(parentNode.to_process_ids) &&
        parentNode.to_process_ids.includes(delegatedTask.id)
      ) {
        parentNode.to_process_ids = parentNode.to_process_ids.map((pid) =>
          pid === delegatedTask.id ? delegationNodeId : pid,
        )
        parentNode.modified_at = delegation.delegated_at
        replacedInParent = true
      }
    })

    // 2. If parent was not found via to_process_ids, check existingFromIds
    if (!replacedInParent && existingFromIds.length > 0) {
      existingFromIds.forEach((fromId) => {
        const prev = finalPdData.find((it) => it.id == fromId)
        if (prev) {
          prev.to_process_ids = prev.to_process_ids
            ? [...prev.to_process_ids.filter((pid) => pid !== delegatedTask.id), delegationNodeId]
            : [delegationNodeId]
          prev.modified_at = delegation.delegated_at
        }
      })
    }

    // 3. Create delegation node with all required process metadata
    const delegationNode = {
      ...delegation,
      process_reference_num: delegatedTask.process_reference_num,
      process_template_id: delegatedTask.process_template_id,
      prefix: delegatedTask.prefix,
      activity_type: delegatedTask.activity_type,
      version: delegatedTask.version,
      data: delegatedTask.data,
      activity_template_id: delegatedTask.activity_template_id,
      activity_template_name: delegatedTask.activity_template_name,
      created_at: delegatedTask.created_at,
      modified_at: delegation.delegated_at,
      created_by: delegation.delegated_by,
      modified_by: delegation.delegated_to,
      from_process_ids: existingFromIds,
      to_process_ids: [delegatedTask.id],
      id: delegationNodeId,
      status: 'delegated',
      assignee: delegation.delegated_by,
    }

    // 4. Update delegated task
    delegatedTask.from_process_ids = [delegationNodeId]
    delegatedTask.created_at = delegation.delegated_at
    if (delegation.delegated_to) {
      delegatedTask.assignee = delegation.delegated_to
    }

    finalPdData.push(delegationNode)
  })

  return finalPdData
}

const ParallelRouteTimeline = ({
  processReferenceNum,
  processTemplateId,
  openProcessData = () => {},
  fieldOfDMS = {},
  isDMS = false,
  onDeleteFromNode,
}) => {
  const navigate = useNavigate()
  const { user } = useAuthContext()
  const [pdDataFinal, setPdDataFinal] = useState([])
  const [docMap, setDocMap] = useState({})

  const buildPairs = (tpl, ref) => {
    if (Array.isArray(tpl) && Array.isArray(ref)) {
      return tpl
        .map((t, i) => ({
          process_template_id: Number(t),
          process_reference_num: Number(ref[i]),
        }))
        .filter(
          (p) => !Number.isNaN(p.process_template_id) && !Number.isNaN(p.process_reference_num),
        )
    }

    const t = Number(tpl)
    const r = Number(ref)

    if (!Number.isNaN(t) && !Number.isNaN(r)) {
      return [{ process_template_id: t, process_reference_num: r }]
    }

    return []
  }

  const pairs = useMemo(
    () => buildPairs(processTemplateId, processReferenceNum),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(processTemplateId), JSON.stringify(processReferenceNum)],
  )

  useEffect(() => {
    if (!user || !pairs.length) return
    ;(async () => {
      const { pdData, delegationTrackerData, docMap } = await timelineService.getTimelineData(
        user,
        pairs,
      )

      const finalPdData = considerDelegation(pdData, delegationTrackerData)
      setPdDataFinal(finalPdData)
      setDocMap(docMap)
    })()
  }, [user, pairs])

  const workOnTask = (pd) => {
    navigate(`/process-data/${pd.id}/${pd.activity_template_id}`)
  }

  return (
    <Workflow
      nodes={pdDataFinal}
      openProcessData={openProcessData}
      openTask={workOnTask}
      docMap={docMap}
      isDMS={isDMS}
      fieldOfDMS={fieldOfDMS}
      onDeleteFromNode={onDeleteFromNode}
    />
  )
}

export default ParallelRouteTimeline
